import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { formatApiError } from "../lib/customerApi";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
function startGoogleSignIn(intendedRedirect) {
  sessionStorage.setItem("post_google_redirect", intendedRedirect || "/account");
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const redirect = new URLSearchParams(location.search).get("redirect") || "/account";
  const { login, register } = useCustomerAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back");
      } else {
        await register({ email, password, name, phone });
        toast.success("Account created");
      }
      navigate(redirect);
    } catch (err) {
      toast.error(formatApiError(err, isLogin ? "Invalid credentials" : "Could not create account"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16" data-testid={`customer-${mode}-page`}>
      <div className="w-full max-w-md">
        <p className="eyebrow text-center mb-3">{isLogin ? "Welcome back" : "Create account"}</p>
        <h1 className="font-serif-display text-5xl text-center">
          {isLogin ? "Sign in" : "Join Asvasa"}
        </h1>
        <p className="text-center text-[#4A4A4A] mt-4 text-sm">
          {isLogin
            ? "Access your orders, wishlist and preferences."
            : "Save your wishlist, track orders and enjoy members-only editions."}
        </p>

        <form onSubmit={submit} className="mt-10 space-y-5">
          {/* ─── Continue with Google (Emergent-managed OAuth) ─── */}
          <button
            type="button"
            onClick={() => startGoogleSignIn(redirect)}
            className="w-full flex items-center justify-center gap-3 bg-white border border-[#1A1A1A]/25 hover:border-[#1A1A1A] hover:shadow-md py-3.5 text-[13px] text-[#1A1A1A] transition-all font-medium"
            data-testid="google-signin-btn"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path fill="#4285F4" d="M19.6 10.23c0-.68-.06-1.34-.18-1.98H10v3.74h5.4a4.62 4.62 0 0 1-2 3.04v2.52h3.24c1.9-1.75 3-4.32 3-7.32z"/>
              <path fill="#34A853" d="M10 20c2.7 0 4.96-.9 6.6-2.44l-3.23-2.52c-.9.6-2.04.96-3.37.96-2.6 0-4.8-1.75-5.58-4.1H1.06v2.6A9.98 9.98 0 0 0 10 20z"/>
              <path fill="#FBBC05" d="M4.42 11.9a6 6 0 0 1 0-3.8V5.5H1.06a10 10 0 0 0 0 9l3.36-2.6z"/>
              <path fill="#EA4335" d="M10 3.96c1.47 0 2.78.5 3.82 1.5l2.86-2.86A9.94 9.94 0 0 0 10 0 9.98 9.98 0 0 0 1.06 5.5l3.36 2.6C5.2 5.72 7.4 3.96 10 3.96z"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 py-1">
            <span className="flex-1 h-px bg-[#1A1A1A]/15" />
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#1A1A1A]/50">or with email</span>
            <span className="flex-1 h-px bg-[#1A1A1A]/15" />
          </div>
          {!isLogin && (
            <>
              <Field label="Full name">
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="auth-name"
                  className={inputCls}
                />
              </Field>
              <Field label="Phone (optional)">
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="auth-phone"
                  className={inputCls}
                />
              </Field>
            </>
          )}
          <Field label="Email">
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email"
              className={inputCls}
            />
          </Field>
          <Field label="Password">
            <input
              required
              type="password"
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password"
              className={inputCls}
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1A1A1A] text-[#c3ba9e] py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-black disabled:opacity-60"
            data-testid="auth-submit"
          >
            {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </button>
        </form>

        <p className="text-center mt-8 text-sm text-[#4A4A4A]">
          {isLogin ? (
            <>
              New to Asvasa?{" "}
              <Link to={`/signup?redirect=${encodeURIComponent(redirect)}`} className="text-[#1A1A1A] underline" data-testid="auth-switch-signup">
                Create an account
              </Link>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="text-[#1A1A1A] underline" data-testid="auth-switch-login">
                Sign in
              </Link>
            </>
          )}
        </p>
        {isLogin && (
          <p className="text-center mt-3 text-sm">
            <Link to="/forgot-password" className="text-[#7A7A7A] underline-offset-4 hover:underline">Forgot your password?</Link>
          </p>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-[#a89e80] bg-transparent px-4 py-3 outline-none focus:border-[#1A1A1A] text-[15px]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}
